import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { NAVIGATION } from '@nykaa/nes-ui-components/BottomSheet';
import {
  API_PARAM_KEYS,
  SCREEN_TYPE,
} from '@nykaa/nes-auth-shared/store/constants';
import AuthBottomSheet from './components/AuthBottomSheet';
import { replaceAuthScreen } from '@nykaa/nes-auth-shared/store/utils';
import useAuthData from '@nykaa/nes-auth-shared/hooks/useAuthData';
import useBackNavigation from '@nykaa/nes-auth-shared/hooks/useBackNavigation';
import {
  VerifyScreenProps,
  WelcomeScreenProps,
} from '@nykaa/nes-auth-shared/types';
import {
  OTP_SENT_TEXT,
  OTP_VERIFICATION,
  PROFILE_SCREEN_TITLE,
  VERIFY_ACCOUNT,
  WELCOME_SCREEN_TITLE,
} from '@nykaa/nes-auth-shared/constants/strings';
import { SUBTITLES } from '@nykaa/nes-auth-shared/constants';
import { useStringFromId } from '@nykaa/nes-intl-shared';
import Portal from '@nykaa/nes-ui-components/Portal';
import { Inventory } from '@nykaa/nes-auth-shared/store/types';
import { State } from 'store/types';
import { hideSignUp } from 'store/app/actions';
import OTPScreen from './components/OTPScreen';
import LinkedEmail from './components/LinkedEmail';
import PasswordLogin from './components/PasswordLogin';
import EmailLogin from './components/Welcome/EmailLogin';
import MobileLogin from './components/Welcome/MobileLogin';
import MobileInfo from './components/Profile/MobileInfo';
import { pushEvent } from '@nykaa/nes-data-layer';
import { SHOW_SMART_BANNER } from 'shared/constants';

const {
  mobileInput,
  emailInput,
  otpReader,
  passwordReader,
  linkEmail,
  profileInfoMobile,
} = SCREEN_TYPE;

interface ViewToRenderProps {
  flowType: string;
  setSuccessMessage: React.Dispatch<React.SetStateAction<string>>;
  showNudge?: boolean;
  bannerWidgets?: Inventory[];
  signupBannerAdplatformEnabled: boolean;
}

const EMAIL_SWITCH_EVENT = 'emailSwitch';
const MOBILE_SWITCH_EVENT = 'mobileSwitch';
const ViewToRender = ({
  flowType,
  setSuccessMessage,
  showNudge = false,
  bannerWidgets,
  signupBannerAdplatformEnabled,
}: ViewToRenderProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isMobileLogin, setIsMobileLogin] = useState(true);

  const { mobileNumber } = API_PARAM_KEYS;
  const creativeUrl = useSelector(
    (state: State) => state.remoteConfig.configs.signUpCreative
  );
  const { hideSocialLogin, subTitle: subTitleMsg } = useSelector(
    (state: State) => state.remoteConfig?.configs?.signUpNudge || {}
  );

  const {
    origin,
    email,
    mobile,
    loading,
    sendOtpToMail,
    selectedCustomerId,
    linkedEmail,
  } = useAuthData();
  const isEmail = origin !== mobileNumber || selectedCustomerId;
  let emailPhone = isEmail ? email : mobile;
  if (sendOtpToMail) {
    emailPhone = linkedEmail || email;
  }

  const handleSwitch = useCallback(() => {
    setIsMobileLogin(!isMobileLogin);
    let stepName = mobileInput;
    let event = EMAIL_SWITCH_EVENT;
    if (isMobileLogin) {
      stepName = emailInput;
      event = MOBILE_SWITCH_EVENT;
    }
    pushEvent(event);
    replaceAuthScreen({
      dispatch,
      positionOfInterest: 0,
      stepData: {
        stepName: stepName,
      },
      authResponseData: { email: '', mobile: '', message: '' },
    });
  }, [isMobileLogin, dispatch]);

  let View = MobileLogin;
  let welcomeScreenProps: WelcomeScreenProps = {
    switchBtnCallback: handleSwitch,
    showNudge,
    hideSocialLogin,
  };

  const { handleOtpBack, handlePasswordBack, handleProfileBack } =
    useBackNavigation(history, dispatch);

  const {
    welcomeScreen,
    linkedEmail: linkedEmailSubTitle,
    password,
    profileScreen,
  } = SUBTITLES;

  // Translate all constants
  const welcomeScreenTitleText = useStringFromId(WELCOME_SCREEN_TITLE, 'Login or Signup');
  const otpVerificationText = useStringFromId(OTP_VERIFICATION, 'OTP Verification');
  const verifyAccountText = useStringFromId(VERIFY_ACCOUNT, 'Verify Account');
  const profileScreenTitleText = useStringFromId(PROFILE_SCREEN_TITLE, 'Help us Know You Better');
  const otpSentText = useStringFromId(OTP_SENT_TEXT, 'Enter OTP received on');
  const welcomeScreenSubtitleText = useStringFromId(welcomeScreen, 'Get started & grab best offers on top brands!');
  const linkedEmailSubtitleText = useStringFromId(linkedEmailSubTitle, 'This mobile is not registered with us. However we found the following email(s) associated with this mobile');
  const passwordSubtitleText = useStringFromId(password, 'Enter your Nykaa password to proceed');
  const profileScreenSubtitleText = useStringFromId(profileScreen, 'A step closer to shopping with exciting offers');

  let Props: WelcomeScreenProps | VerifyScreenProps = { showNudge };
  let goBack = () => {};
  let navigation: keyof typeof NAVIGATION = showNudge
    ? NAVIGATION.handle
    : NAVIGATION.none;
  let title = welcomeScreenTitleText;
  let subTitle = subTitleMsg || welcomeScreenSubtitleText;
  let banner = '';
  let height = 'auto';
  switch (flowType) {
    case mobileInput:
      View = MobileLogin;
      Props = welcomeScreenProps;
      if (showNudge) {
        banner = creativeUrl;
      }
      break;
    case emailInput:
      View = EmailLogin;
      Props = welcomeScreenProps;
      if (showNudge) {
        banner = creativeUrl;
      }
      break;
    case linkEmail:
      if (mobile) {
        View = LinkedEmail;
        Props = welcomeScreenProps;
        subTitle = linkedEmailSubtitleText;
      }
      break;
    case otpReader:
      View = OTPScreen;
      title = otpVerificationText;
      subTitle = `${otpSentText} ${emailPhone}`;
      height = '436px';
      Props = { ...welcomeScreenProps, setSuccessMessage };
      goBack = handleOtpBack;
      navigation = NAVIGATION.back;
      break;
    case passwordReader:
      View = PasswordLogin;
      title = verifyAccountText;
      subTitle = passwordSubtitleText;
      Props = { ...welcomeScreenProps, setSuccessMessage };
      goBack = handlePasswordBack;
      navigation = NAVIGATION.back;
      break;
    case profileInfoMobile:
      View = MobileInfo;
      title = profileScreenTitleText;
      Props = { ...welcomeScreenProps, setSuccessMessage };
      subTitle = profileScreenSubtitleText;
      goBack = handleProfileBack;
      navigation = NAVIGATION.back;
      break;
  }
  const handleNavgation = () => {
    if (navigation === NAVIGATION.back) {
      goBack();
    } else {
      dispatch(hideSignUp(history));
      pushEvent(SHOW_SMART_BANNER);
    }
  };

  return (
    <Portal>
      <AuthBottomSheet
        isLoading={loading}
        isNudge={showNudge}
        handleNavigation={handleNavgation}
        navigation={navigation}
        title={title}
        subTitle={subTitle}
        banner={banner}
        height={height}
        bannerWidgets={bannerWidgets}
        signupBannerAdplatformEnabled={signupBannerAdplatformEnabled}
      >
        <View {...Props} />
      </AuthBottomSheet>
    </Portal>
  );
};

export default ViewToRender;
